import * as React from 'react';
import { __ } from 'react-i18n';
import {
  FlexCol,
  FlexRow,
  FlexRowCenter,
  FlexRowCenterVertical,
} from '../../Defaults/Flexbox/Flexbox';
import styled, { withTheme } from 'styled-components';
import { rem } from 'polished';
import { FormInput } from '../Form/FormInput';
import { IconType, SvgIcon } from '../General/SvgIcon';
import { InfoText, InfoType } from '../General/InfoText';
import { prop } from '../../../utilities';

export const zonePricesMap = {
  1: { spoj: 150, vykr: 200 },
  2: { spoj: 200, vykr: 300 },
  3: { spoj: 250, vykr: 350 },
  4: { spoj: 250, vykr: 350 },
};

interface Props {
  theme: any;
  zoneId: number;
  handleInputChange: any;
  zip: number;
  hasFreeDelivery: boolean;
  priceNeeded: number;
  percentage: number;
}

function FreeDelivery({
  theme,
  zoneId,
  handleInputChange,
  zip,
  hasFreeDelivery,
  priceNeeded,
  percentage,
}: Props) {
  const prices = zoneId ? zonePricesMap[zoneId] : null;
  const spojPrice = prop(prices, 'spoj');
  const vykrPrice = prop(prices, 'vykr');

  React.useEffect(() => {
    if (zip) {
      handleInputChange({ target: { value: zip } });
    }
  }, [zip]);

  return (
    <Wrapper>
      <InputInfoWrapper>
        <FormInput
          name={'zip'}
          type="text"
          label={__('Zadajte adresu doručenia')}
          maxWidthPx={320}
          onChange={handleInputChange}
          defaultValue={zip ? zip : ''}
        />
        <SvgIcon
          width={24}
          height={24}
          icon={IconType.success}
          alt={__('Zadajte svoju PSČ')}
          title={__('Zadajte svoju PSČ')}
          fill={theme.colors.borderColor}
          marginLeft={8}
        />
      </InputInfoWrapper>

      {hasFreeDelivery ? (
        <FreeDelWrapper>
          {' '}
          <SvgIcon
            width={24}
            height={24}
            viewBox="0 0 11 8"
            icon={IconType.checked}
            alt={__('Dopravu máte zadarmo')}
            fill={theme.colors.stockCountAvailable}
            marginRight={16}
          />
          <span>{__('Dopravu máte zadarmo')}</span>
        </FreeDelWrapper>
      ) : zoneId ? (
        <>
          <DesktopWrapper>
            <FreeDeliveryInfoWrapper>
              <PriceLeftWrapper>
                <FreeDelInfo>
                  {__('Pre')} <Semibold>{__('dopravu zadarmo')}</Semibold>{' '}
                  {__('musíte splniť jednu z podmienok')}
                </FreeDelInfo>
                <PriceLeft>{`${__('Zostáva')} ${priceNeeded} €`}</PriceLeft>
              </PriceLeftWrapper>
              <Progress width={percentage} />
              {vykrPrice ? (
                <ChecklistInfo>
                  <SvgIcon
                    width={11}
                    height={8}
                    viewBox="0 0 11 8"
                    icon={IconType.checked}
                    alt=""
                    fill={theme.colors.stockCountAvailable}
                    marginRight={12}
                  />
                  {__('Nakúpte vykurovaciu techniku')}
                  <ValueText>{`v hodnote viac ako ${vykrPrice} €`}</ValueText>
                </ChecklistInfo>
              ) : null}
              {spojPrice ? (
                <ChecklistInfo>
                  <SvgIcon
                    width={11}
                    height={8}
                    viewBox="0 0 11 8"
                    icon={IconType.checked}
                    alt=""
                    fill={theme.colors.stockCountAvailable}
                    marginRight={12}
                  />
                  {__('Nakúpte spojovací a ostatný materiál')}
                  <ValueText>{`v hodnote viac ako ${spojPrice} €`}</ValueText>
                </ChecklistInfo>
              ) : null}
            </FreeDeliveryInfoWrapper>
          </DesktopWrapper>
          <MobileWrapper>
            <FreeDeliveryInfoWrapper>
              <PriceLeftWrapper>
                <FreeDelInfo>
                  {__('Pre')} <Semibold>{__('dopravu zadarmo')}</Semibold>{' '}
                  {__('musíte splniť jednu z podmienok')}
                </FreeDelInfo>
                <SvgIcon
                  cursor={'pointer'}
                  width={24}
                  height={24}
                  icon={IconType.success}
                  alt={__('Informácie o zadaní PSČ')}
                  title={__('Informácie o zadaní PSČ')}
                  fill={theme.colors.borderColor}
                  marginLeft={16}
                />
              </PriceLeftWrapper>
              <Progress width={percentage} />
              <PriceLeft>{`${__('Zostáva')} ${priceNeeded} €`}</PriceLeft>
            </FreeDeliveryInfoWrapper>
          </MobileWrapper>
        </>
      ) : (
        <InfoText
          info={__('Zadajte prosím platné PSČ')}
          type={InfoType.INFO}
          marginBottom={0}
          noIcon={true}
        />
      )}
    </Wrapper>
  );
}

const Wrapper = styled(FlexRow)`
  padding: ${rem(32)};
  align-items: center;
  border: ${({ theme }) => theme.borders.primary};
  margin-bottom: ${rem(56)};

  ${({ theme }) => theme.mediab.m810`
    flex-flow: column;
    border: 0;
    padding-top: 0;
    border-bottom: ${theme.borders.primary};
    margin-bottom: 0;
  `}

  ${({ theme }) => theme.mediab.s450`
    margin: 0 -${rem(16)};
    padding:  0 ${rem(16)} ${rem(32)};
    max-width: initial;
    width: calc(100vw - ${rem(16)});
  `}
`;

const DesktopWrapper = styled.div`
  width: 100%;
  ${({ theme }) => theme.mediab.m810`
    display: none;
  `}
`;

const MobileWrapper = styled.div`
  display: none;
  ${({ theme }) => theme.mediab.m810`
    display: block;
  `}
`;

const InputInfoWrapper = styled(FlexRowCenter)`
  width: 100%;
  max-width: ${rem(350)};
  margin-right: ${rem(96)};

  ${({ theme }) => theme.mediab.m810`
   margin-right: 0;
  `}
`;

const FreeDeliveryInfoWrapper = styled(FlexCol)`
  width: 100%;
  max-width: ${rem(568)};
`;

const Text = styled.span`
  font-size: ${rem(14)};
  font-family: ${({ theme }) => theme.font.primary};
  color: ${({ theme }) => theme.colors.textPrimary};
  line-height: ${rem(22)};
`;

const FreeDelInfo = styled.span`
  margin: 0;
  font-size: ${rem(12)};
  font-family: ${({ theme }) => theme.font.tertiary};
  line-height: ${rem(20)};
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const PriceLeftWrapper = styled(FlexRowCenterVertical)`
  justify-content: space-between;
  margin-bottom: ${rem(8)};
`;

const PriceLeft = styled(Text)`
  color: ${({ theme }) => theme.colors.placeholder};
  white-space: nowrap;
  margin-left: ${rem(8)};
  font-family: ${({ theme }) => theme.font.tertiary};
  font-size: ${rem(12)};

  ${({ theme }) => theme.mediab.m810`
   text-align: right;
  `}
`;

const Semibold = styled(FreeDelInfo)`
  font-weight: 600;
  font-family: ${({ theme }) => theme.font.tertiary};
`;

const ChecklistInfo = styled(FlexRowCenterVertical)`
  margin-bottom: ${rem(8)};
  font-size: ${rem(14)};
  font-weight: 400;
  font-family: ${({ theme }) => theme.font.secondary};
  color: ${({ theme }) => theme.colors.textPrimary};
  line-height: ${rem(22)};

  ${({ theme }) => theme.mediab.m810`
   display: none;
  `}
`;

const ValueText = styled(Text)`
  font-family: ${({ theme }) => theme.font.secondary};
  margin-left: ${rem(4)};
`;

const Progress = styled.div<{ width: number }>`
  width: 100%;
  height: ${rem(4)};
  position: relative;
  margin-bottom: ${rem(16)};
  border-radius: ${rem(2)};
  background: #eeeeee;

  &:after {
    content: '';
    position: absolute;
    background: ${({ theme }) => theme.colors.stockCountAvailable};
    top: 0;
    bottom: 0;
    left: 0;
    width: ${({ width }) => `${100 - width}%`};
  }

  ${({ theme }) => theme.mediab.m810`
   margin-bottom: ${rem(8)};
  `}
`;

const FreeDelWrapper = styled(FlexRowCenterVertical)`
  font-family: ${({ theme }) => theme.font.secondary};
  color: ${({ theme }) => theme.colors.stockCountAvailable};
  font-size: ${rem(14)};
`;

const WithTheme: any = withTheme(FreeDelivery);
export { WithTheme as FreeDelivery };
